import { FileInfo } from "../../../api/admin/models_admin";
import { displayFileName } from "../components";

/**
 * 「編集を完了」を押すまで実行を保留しておくファイル操作。
 *
 * アップロード・削除を1件ずつ即時実行すると、そのたびにインデクサを走らせる必要が生じ、
 * AI Searchのインデクサが同時実行できない(409になる)ため、操作をここへ溜めてから
 * 「削除 → アップロード → インデクサ実行」の順にまとめて実行する。
 */
export type PendingOp =
    | { kind: "delete", target: FileInfo }
    | { kind: "upload", file: File }

// キーは表示上のファイル名(拡張子まで含む)。Blobはファイル名で一意なため、これで同名を突き合わせられる
export type PendingOps = Map<string, PendingOp>

export const pendingOpKeyOfFile = (file: FileInfo) => displayFileName(file)

/**
 * 削除を保留に積む。
 * 同名の保留があった場合は「最後のアクションだけを実際に実行する」ように畳む:
 *  - アップロード(新規)を積んだ後の削除 … アップロード自体が不要になるので保留ごと取り消す
 *  - アップロード(上書き)を積んだ後の削除 … 元からあるファイルの削除だけを行う
 */
export const queueDelete = (ops: PendingOps, key: string, serverFile: FileInfo | undefined): PendingOps => {
    const next = new Map(ops)
    if (serverFile === undefined) {
        next.delete(key)
    } else {
        next.set(key, { kind: "delete", target: serverFile })
    }
    return next
}

/**
 * アップロードを保留に積む。
 * 同名の削除が積まれていた場合はこの代入で上書きされる。アップロードはBlobを上書きし、
 * 整形結果も破棄する(api_upload内のclear_preprocess_info)ため、先行する削除は不要になる。
 */
export const queueUpload = (ops: PendingOps, file: File): PendingOps => {
    const next = new Map(ops)
    next.set(file.name, { kind: "upload", file })
    return next
}

export const cancelPending = (ops: PendingOps, key: string): PendingOps => {
    const next = new Map(ops)
    next.delete(key)
    return next
}

/** 実際に実行する操作。削除を先に行ってからアップロードする */
export interface ApplyPlan {
    deletions: FileInfo[]
    uploads: File[]
}

export const buildApplyPlan = (ops: PendingOps): ApplyPlan => {
    const deletions: FileInfo[] = []
    const uploads: File[] = []
    for (const op of ops.values()) {
        if (op.kind === "delete") {
            deletions.push(op.target)
        } else {
            uploads.push(op.file)
        }
    }
    return { deletions, uploads }
}

/** 一覧に表示する行。サーバ上のファイルと保留中の操作をファイル名で突き合わせたもの */
export interface FileRow {
    key: string
    // サーバ上のファイル。まだアップロードしていない新規ファイルの行ではundefined
    file?: FileInfo
    pending?: PendingOp
}

// none:変更なし / delete:削除予定 / overwrite:上書き予定 / upload:追加予定
export type FileRowState = "none" | "delete" | "overwrite" | "upload"

export const fileRowState = (row: FileRow): FileRowState => {
    if (row.pending === undefined) return "none"
    if (row.pending.kind === "delete") return "delete"
    return row.file === undefined ? "upload" : "overwrite"
}

// 「状態」列の並び順。これから実行する操作を確認したい列なので、
// 昇順で保留中の操作が先頭に集まり、変更なしが末尾に来るようにする
const fileRowStateOrders: Record<FileRowState, number> = {
    upload: 0,
    overwrite: 1,
    delete: 2,
    none: 3
}

export const fileRowStateOrder = (row: FileRow): number => fileRowStateOrders[fileRowState(row)]

export const buildFileRows = (files: FileInfo[], ops: PendingOps): FileRow[] => {
    const rows: FileRow[] = files.map(file => {
        const key = pendingOpKeyOfFile(file)
        return { key, file, pending: ops.get(key) }
    })

    // サーバ上に無いファイル(新規アップロード予定)は、一覧に行が無いのでここで足す
    const serverKeys = new Set(rows.map(row => row.key))
    for (const [key, pending] of ops) {
        if (serverKeys.has(key)) continue
        rows.push({ key, pending })
    }
    return rows
}

export const fileRowSize = (row: FileRow): number => {
    if (row.pending?.kind === "upload") return row.pending.file.size
    return row.file?.size ?? 0
}

export const fileRowType = (row: FileRow): string => {
    if (row.file !== undefined) return row.file.type
    const dotIndex = row.key.lastIndexOf('.')
    return dotIndex < 0 ? "" : row.key.slice(dotIndex + 1)
}
